import * as Sentry from '@sentry/nextjs'
import {
  CardElement,
  ThreeDSecureAction,
  useRecurly,
} from '@recurly/react-recurly'
import React, { MutableRefObject, RefObject, useEffect, useState } from 'react'
import { RecurlyError, TokenPayload } from '@recurly/recurly-js'
import Spinner from '../../components/Spinner'
import { RecurlyAccount } from '../../types/types'
import PayPalButton from './PaypalButton'
import countries from '../../data/countries.json'
import usStates from '../../data/states.us.json'
import caStates from '../../data/states.ca.json'
import styles from '../../styles/Recurly.module.scss'
import { formatRecurlyError } from '../Subscription/SubscriptionHelpers'

// Recurly requires these countries to also specify state/province
const requiresStateField = ['US', 'CA', 'IT', 'NL']

interface propTypes {
  setAccount: Function
  togglePaymentForm: Function
  account: RecurlyAccount
  onSubmit: (token: TokenPayload) => Promise<void>
  formRef?: RefObject<HTMLFormElement>
}

export const AddPaymentDetailsForm = (props: propTypes) => {
  const { setAccount, togglePaymentForm, account, onSubmit, formRef } = props
  const recurly = useRecurly()
  recurly.configure(process.env.NEXT_PUBLIC_RECURLY_PUBLIC_KEY!)
  const [loading, setLoading] = useState(false)
  const [states, setStates] = useState<{ code: string; name: string }[]>([])
  const [htmlErrorMessage, setHtmlErrorMessage] = useState<JSX.Element>(<></>)

  useEffect(() => {
    setStates(account.country === 'US' ? usStates : caStates)
  }, [setStates, account.country])

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    setHtmlErrorMessage(<></>)
    recurly.token(event.currentTarget, async (err, token) => {
      if (err) {
        // Keeping this to see what recurly js gets in dev/prod
        console.error(JSON.stringify(err))
        setHtmlErrorMessage(formatRecurlyError(err))
        setLoading(false)
      } else {
        onSubmit(token)
          .then(() => setLoading(false))
          .catch((error) => {
            if ((error as any).message) {
              setHtmlErrorMessage(<>{(error as any).message}</>)
            }
            setLoading(false)
          })
      }
    })
  }

  function onInputChange(key: string) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setAccount({ ...account, [key]: e.target.value })
    }
  }

  function onCountryChange(country: string) {
    setStates(country === 'US' ? usStates : caStates)
    setAccount({ ...account, country: country })
  }

  return (
    <div className="row">
      <div className={`${styles.recurly} col-lg-6 mb-20`}>
        <div className="mb-20">
          <strong>Add new credit card</strong>
        </div>
        <form
          className={`form-group position-relative`}
          ref={formRef}
          onSubmit={handleSubmit}>
          {loading && (
            <div className={`mb-20 ${styles.spinnerOverlay}`}>
              <Spinner />
            </div>
          )}
          <div className={loading ? styles.blockAndBlur : ''}>
            <div className="row mb-15">
              <div className="col-6">
                <input
                  className="form-control"
                  type="text"
                  data-recurly="first_name"
                  placeholder="First name"
                  onChange={onInputChange('firstName')}
                  value={account.firstName || ''}
                />
              </div>
              <div className="col-6">
                <input
                  className="form-control"
                  type="text"
                  data-recurly="last_name"
                  placeholder="Last name"
                  onChange={onInputChange('lastName')}
                  value={account.lastName || ''}
                />
              </div>
            </div>

            <input
              className="form-control mb-15"
              type="text"
              data-recurly="address1"
              placeholder="Address"
            />

            <div className="row mb-15">
              <div className="col-6">
                <input
                  className="form-control"
                  type="text"
                  data-recurly="postal_code"
                  placeholder="Postal Code"
                />
              </div>
              <div className="col-6">
                <input
                  className="form-control"
                  type="text"
                  data-recurly="city"
                  placeholder="City"
                />
              </div>
            </div>

            <div className="row mb-15">
              <div className="col">
                <select
                  className="form-control"
                  data-recurly="country"
                  onChange={(e) => onCountryChange(e.target.value)}
                  value={account.country || ''}>
                  <option value="" disabled hidden>
                    Country
                  </option>
                  {countries.map((country) => {
                    return (
                      <option key={country.name} value={country.code}>
                        {country.name}
                      </option>
                    )
                  })}
                </select>
              </div>
              {account.country &&
                requiresStateField.includes(account.country.toUpperCase()) && (
                  <>
                    <div className="col-4">
                      {account.country === 'IT' || account.country === 'NL' ? (
                        <input
                          className="form-control"
                          type="text"
                          data-recurly="state"
                          placeholder="State/Province"
                          maxLength={30}
                        />
                      ) : (
                        <select className={`form-control`} data-recurly="state">
                          <option value="" disabled hidden>
                            --
                          </option>
                          {states.map((state) => {
                            return (
                              <option key={state.code} value={state.code}>
                                {state.name}
                              </option>
                            )
                          })}
                        </select>
                      )}
                    </div>
                  </>
                )}
            </div>

            <div className="mb-15">
              <CardElement style={{ fontSize: '1em' }} />
            </div>
            <div className="text-danger font-weight-bold small mb-10">
              {htmlErrorMessage}
            </div>
            {!formRef && (
              <div className="d-flex justify-content-between">
                <button
                  className="btn btn-light"
                  onClick={() => togglePaymentForm()}>
                  Cancel
                </button>
                <input type="submit" value="Save" className="btn btn-primary" />
              </div>
            )}
          </div>
        </form>
      </div>

      <div
        className={`${styles.paypal} col-lg-6 d-flex flex-column align-items-center justify-content-center`}>
        <strong className="mb-10">Connect with Paypal</strong>
        <PayPalButton recurly={recurly} update={onSubmit} />
      </div>
    </div>
  )
}
